import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import LoadingScreen from "./LoadingScreen";
import { useAuth } from "../context/AuthContext";
import { useOrganization } from "../context/OrganizationContext";

const GoogleAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { reloadOrganizations } = useOrganization();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const error = searchParams.get("error");
    if (error) {
      toast.error(decodeURIComponent(error));
      navigate("/login", { replace: true });
      return;
    }

    const token = searchParams.get("token");
    const email = searchParams.get("email");
    const name = searchParams.get("name");
    const userId = searchParams.get("user_id");
    const userUuid = searchParams.get("user_uuid");

    if (!token || !email) {
      toast.error("Google sign-in failed: missing credentials in callback.");
      navigate("/login", { replace: true });
      return;
    }

    const userData = {
      email,
      name: name || email.split("@")[0],
      role: "user",
      id: userId ? Number(userId) : Date.now(),
      uuid: userUuid || undefined,
      token,
    };

    // Dashboard.jsx gates on localStorage 'userEmail'; Login.jsx writes it
    // alongside saveSession, so mirror that here or the dashboard bounces
    // back to /login on first paint.
    localStorage.setItem("userEmail", email);

    // Backend appends ?is_new=1 when this OAuth callback created the user
    // (vs. a returning sign-in). Stash the flag + name so Dashboard can
    // pop the welcome toast on first paint and clear it.
    const isNew = searchParams.get("is_new") === "1";
    // ?terms_pending=1 means the new user came in via the Login page (no
    // Terms checkbox) and needs the welcome-terms interstitial before the
    // dashboard. Signup-page OAuth users already accepted Terms via the
    // signed OAuth state — they go straight to /dashboard with a toast.
    const termsPending = searchParams.get("terms_pending") === "1";
    if (isNew) {
      localStorage.setItem("welcomeNewUser", "1");
      localStorage.setItem("welcomeNewUserName", userData.name || "");
    }

    (async () => {
      try {
        await login(userData, token);
        await reloadOrganizations();
        if (termsPending) {
          // The interstitial owns the welcome message + onward navigation.
          navigate("/welcome-terms", { replace: true });
          return;
        }
        if (!isNew) toast.success("Signed in with Google");
        navigate("/dashboard", { replace: true });
      } catch (err) {
        console.error("Google callback error:", err);
        toast.error("Could not finish Google sign-in. Please try again.");
        navigate("/login", { replace: true });
      }
    })();
  }, [searchParams, navigate, login, reloadOrganizations]);

  return <LoadingScreen title="Signing you in" subtitle="Finishing Google sign-in…" />;
};

export default GoogleAuthCallback;
